import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { Add as AddIcon, Download as DownloadIcon, Image as ImageIcon, Delete as DeleteIcon } from "@mui/icons-material";
import JSZip from "jszip";
import saveAs from "file-saver";
import { ComplaintImage } from "../BookingComplaint";
import { ImageSelectionModal } from "./ImageSelectionModal";
import { ProgressDialog } from "../../../../components/notifications/progressDialog/progressDialog";
import ErrorDialog from "../../../../components/notifications/errorDialog/ErrorDialog";
import { RequestStatus } from "../../../../logic/api";
import { ComplaintImageBox } from "./ComplaintImageBox";
import { useTranslation } from "react-i18next";

interface ComplaintImagesSectionProps {
    complaintImages: ComplaintImage[];
    availableInspectionImages: ComplaintImage[];
    selectedImages: ComplaintImage[];
    uploadingImages: Set<string>;
    deletingImageId: string | null;
    isUploading: boolean;
    onOpenImageModal: () => void;
    onImageModalSave: (selectedImages: ComplaintImage[]) => Promise<void>;
    onImageModalCancel: () => void;
    onImageSelect: (image: ComplaintImage) => void;
    onRemoveImage: (image: ComplaintImage) => void;
    onFilesSelected: (files: FileList) => void;
    showImageModal: boolean;
}

export const ComplaintImagesSection: React.FC<ComplaintImagesSectionProps> = ({
    complaintImages,
    availableInspectionImages,
    selectedImages,
    uploadingImages,
    deletingImageId,
    isUploading,
    onOpenImageModal,
    onImageModalSave,
    onImageModalCancel,
    onImageSelect,
    onRemoveImage,
    onFilesSelected,
    showImageModal
}) => {
    const [showImageDownloadProgress, setShowImageDownloadProgress] = useState(false);
    const [imageDownloadErr, setImageDownloadErr] = useState<string | null>(null);
    const { t } = useTranslation();

    const downloadComplaintImages = async () => {
        if (complaintImages.length === 0) return;

        setShowImageDownloadProgress(true);
        const zip = new JSZip();

        try {
            const imageFetchPromises = complaintImages.map(async (image) => {
                const response = await fetch(image.url);
                if (!response.ok) throw new Error(`Failed to fetch ${image.name}`);
                const blob = await response.blob();
                zip.file(`${image.name}`, blob);
            });

            await Promise.all(imageFetchPromises);
            const content = await zip.generateAsync({ type: "blob" });
            saveAs(content, `complaint_images.zip`);
        } catch (error: unknown) {
            setImageDownloadErr(error instanceof Error ? error.message : "Failed to download images");
        } finally {
            setShowImageDownloadProgress(false);
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            {showImageDownloadProgress && <ProgressDialog status={RequestStatus.loading} />}
            {imageDownloadErr && <ErrorDialog err={imageDownloadErr} onClose={() => setImageDownloadErr(null)} />}

            {complaintImages.length === 0 ? (
                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    py: 4,
                    color: "text.secondary"
                }}>
                    <ImageIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {t("complaints.images.noImagesAttached")}
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={onOpenImageModal}
                    >
                        {t("complaints.images.addImages")}
                    </Button>
                </Box>
            ) : (
                <Box>
                    <Box sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        mb: 2
                    }}>
                        {complaintImages.map((image, index) => (
                            <Box
                                key={image.clientId || `image-${index}`}
                                sx={{
                                    "&:hover .delete-button": {
                                        opacity: 1
                                    }
                                }}
                            >
                                <ComplaintImageBox
                                    image={image}
                                    imageHeight={"auto"}
                                    imageFit="scale-down"
                                    onClick={() => window.open(image.url, "_blank")}
                                >
                                    <Button
                                        className="delete-button"
                                        size="small"
                                        variant="contained"
                                        color="error"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRemoveImage(image);
                                        }}
                                        title={t("general.delete")}
                                        data-testid="delete-image-button"
                                        disabled={deletingImageId === image.clientId}
                                        sx={{
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                            minWidth: "auto",
                                            width: 36,
                                            height: 36,
                                            borderRadius: "8px",
                                            opacity: 0,
                                            bgcolor: "rgba(255, 255, 255, 0.95)",
                                            color: "#6b7280",
                                            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                                            border: "1px solid rgba(0, 0, 0, 0.1)",
                                            transition: "all 0.2s ease",
                                            "&:hover": {
                                                bgcolor: "#ef4444",
                                                color: "white",
                                                boxShadow: "0 4px 8px rgba(239, 68, 68, 0.3)",
                                                transform: "scale(1.02)"
                                            },
                                            "&:disabled": {
                                                opacity: 0.5,
                                                transform: "none",
                                                bgcolor: "rgba(255, 255, 255, 0.8)"
                                            }
                                        }}
                                    >
                                        {deletingImageId === image.clientId ? "..." : <DeleteIcon sx={{ fontSize: 14 }} />}
                                    </Button>
                                </ComplaintImageBox>
                            </Box>
                        ))}
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={onOpenImageModal}
                        >
                            {t("complaints.images.addMoreImages")}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={downloadComplaintImages}
                        >
                            {t("complaints.images.downloadAll")}
                        </Button>
                    </Box>
                </Box>
            )}

            <ImageSelectionModal
                open={showImageModal}
                onClose={onImageModalCancel}
                onSave={onImageModalSave}
                complaintImages={complaintImages}
                availableInspectionImages={availableInspectionImages}
                selectedImages={selectedImages}
                uploadingImages={uploadingImages}
                onFilesSelected={onFilesSelected}
                onImageSelect={onImageSelect}
                isUploading={isUploading}
            />
        </Box>
    );
}; 